import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  HeaderTemplate,
  KeyValue,
  MUIDataTable,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import RouteKey from '../../../routes/key';
import { ApplicationType } from '../../../api/candidate/config/model';
import { useGetCoordinatorAppealReview } from '../../../api-refactored/appeal/useGetCoordinatorAppealReview';
import {
  ConfirmAndPayResponse,
  usePostCoordinatorAppealConfirmAndPay,
} from '../../../api-refactored/appeal/usePostCoordinatorAppealConfirmAndPay';
import PaymentButton, { PaymentMethod } from '../../payment/components/PaymentButton';
import { fallbackEmptyString, parseLocationSearch } from '../../../utils/StringUtils';
import PersonalDetails from '../../appeal/components/@candidate/PersonalDetails';
import ExamLevelDetails from '../../appeal/components/@candidate/ExamLevelDetails';
import AppealFees from '../../appeal/components/@candidate/AppealFees';
import { PaymentLinkRequest } from '../../../api/candidate/exam/model';
import { generateColumns, useStyles } from '../../../pages/appeal/CoordinatorReviewAppeal';
import { selectIsUserLoggedIn } from '../../../store/appSlice';
import { useGetUserDetails } from '../../../api-refactored/user/useGetUserDetails';
import { GET_USER_DETAILS_QUERY_KEY } from '../../../constants/queryKeys';
import { usePostValidatePaymentToken } from '../../../api-refactored/payment-link/usePostValidatePaymentToken';
import { GenericErrorResponse } from '../../../api';
import { usePostPaymentAccess } from '../../../api-refactored/financial/usePostPaymentAccess';

const { receipt } = RouteKey.appeal.child;
const { paymentLink } = RouteKey.child;

const { appealRegistration, expired, invalid, used } = RouteKey['payment-link'].child;

const AppealPaymentLink = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const location = useLocation<PaymentLinkRequest>();
  const { token = '' } = parseLocationSearch(location.search);

  const queryClient = useQueryClient();

  const { isLoading: isLoadingUserDetails } = useGetUserDetails();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);

  const [isPaymentDisabled, setIsPaymentDisabled] = useState<boolean>(false);
  const [isSessionDone, setIsSessionDone] = useState<boolean>(false);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  const errorCallback = (err: AxiosError<GenericErrorResponse>) => {
    const errorCode = err.response?.data.code;

    if (errorCode === '1017') {
      history.push(`/${paymentLink}/${appealRegistration}/${used}`);
    } else if (errorCode === '1014') {
      history.push(`/${paymentLink}/${appealRegistration}/${expired}`);
    } else if (errorCode === '1019') {
      history.push(`/${paymentLink}/${appealRegistration}/${invalid}`);
    }
  };

  const invalidSessionCallback = () => {
    setIsSessionDone(true);
    queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
    return false;
  };

  const {
    mutate: invalidateSessionMutate,
    isLoading: isLoadingInvalidateSession,
  } = usePostValidatePaymentToken({
    onError: errorCallback,
    onSettled: () => invalidSessionCallback(),
  });

  const {
    data: reviewDraftData,
    isLoading: isLoadingReviewDraftData,
  } = useGetCoordinatorAppealReview({
    uuid,
    enabled: isSessionDone,
    onSuccess: () =>
      mutatePostPaymentAccess({
        registrationUuid: uuid,
      }),
  });

  const { mutate: mutatePostConfirmAndPay } = usePostCoordinatorAppealConfirmAndPay({
    onSuccess: (res) => {
      if (res.paymentSuccess) {
        history.push(
          `/${paymentLink}/${appealRegistration}/${uuid}/${receipt.route}?internalTransactionRef=${res.internalTransactionRef}`
        );
      }
    },
  });

  const { mutate: mutatePostPaymentAccess } = usePostPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  useEffect(() => {
    if (!isUserLoggedIn && !isLoadingUserDetails) {
      invalidateSessionMutate(token);
    }
  }, [invalidateSessionMutate, isLoadingUserDetails, isUserLoggedIn, token]);

  if (isUserLoggedIn && token) {
    return (
      <ContentTemplate>
        <Typography variant="subtitle1" className={classes.pleaseLogoutText}>
          Authenticated session detected in the current browser. Please log out to proceed.
        </Typography>
        <Button onClick={() => invalidateSessionMutate(token)}>Logout</Button>
      </ContentTemplate>
    );
  }

  const handleClickPaymentButton = (callback: () => void) => {
    callback();
  };

  const handlePaymentOption = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    mutatePostConfirmAndPay(
      {
        uuid,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res: ConfirmAndPayResponse) => {
          callback(res.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  return (
    <>
      <HeaderTemplate>
        {reviewDraftData && (
          <KeyValue
            keyText={t('appeal->>AppealCoordinatorSelectSubject.ExamLevel')}
            valueText={reviewDraftData.selectedSubjects.examLevel}
          />
        )}
      </HeaderTemplate>
      {isLoadingReviewDraftData || isLoadingInvalidateSession || isLoadingUserDetails ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <Box>
          {reviewDraftData && (
            <>
              <ContentTemplate isContinuable type="full">
                <SectionHeader title={t('appeal->>ReviewAppeal.Review Application')} />
                <PersonalDetails {...reviewDraftData.candidateDetails} />
                <Divider />
                <ExamLevelDetails
                  formHeader={t('appeal->>ReviewAppeal.Selected Subject(s)')}
                  {...reviewDraftData.selectedSubjects}
                />
                <Grid sm={12}>
                  <MUIDataTable
                    columns={generateColumns(reviewDraftData, classes, t)}
                    data={reviewDraftData.selectedSubjects.subjectInfo}
                  />
                  <Divider className={classes.divider} />
                </Grid>
                <AppealFees {...reviewDraftData.appealFees} />
              </ContentTemplate>

              <ContentTemplate continuous>
                <Grid
                  container
                  direction="row"
                  alignItems="flex-start"
                  spacing={3}
                  className={classes.buttonContainer}
                >
                  <Grid xs={12} md={4}>
                    <PaymentButton
                      buttonClassName={classes.paymentButton}
                      isUniqueLink={!!token}
                      disabled={isPaymentDisabled}
                      onPaymentComplete={(isRetry) => {
                        setIsPaymentDisabled(isRetry);
                      }}
                      paymentMethodsAvailable={paymentAccessDetailsOption}
                      onClickPaymentButton={(callback) => {
                        handleClickPaymentButton(callback);
                      }}
                      onHandlePaymentMethod={(callback, method) => {
                        handlePaymentOption(callback, method);
                      }}
                      onSuccessPayment={(txnId) =>
                        history.push(
                          `/${paymentLink}/${appealRegistration}/${uuid}/${receipt.route}?internalTransactionRef=${txnId}`
                        )
                      }
                      fullWidth
                      buttonText={t(
                        'appeal->>AppealCoordinatorReviewApplication.ProceedWithAppeal'
                      )}
                      buttonErrorText={t('appeal->>AppealReview.BackToAppealApplications')}
                      buttonVariant="contained"
                      type={ApplicationType.APPEAL}
                    />
                  </Grid>
                </Grid>
              </ContentTemplate>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default AppealPaymentLink;
